import $ from "jquery";

window.$ = window.jQuery = $;

if (!$.parseJSON) {
    $.parseJSON = JSON.parse;
}

import { initValidationRules } from "../../JavaScript/validators";

const validationStackReady = (async () => {
    await import("jquery-validation");
    await import("jquery-validation-unobtrusive");
})();

let validationRulesInitialized = false;

function wantsValidation(form) {
    return form.dataset.sdsFormValidate === "true";
}

function hasValidationStack() {
    return !!($ && $.validator && $.validator.unobtrusive);
}

function ensureValidationRules() {
    if (validationRulesInitialized) return;

    initValidationRules();
    validationRulesInitialized = true;
}

function getValidator(form) {
    return $(form).data("validator");
}

function configureValidator(form) {
    const validator = getValidator(form);

    if (!validator?.settings) return;

    validator.settings.ignore = [
        ":hidden:not([data-sds-validate-hidden='true'])",
        "[disabled]",
        "[readonly]",
        "[data-val='false']",
        "[data-upload-input]",
        ":not([data-val='true']):not([data-rule-required]):not([required])",
    ].join(",");
}

async function ensureParsed(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (!wantsValidation(form)) return false;

    await validationStackReady;

    if (!hasValidationStack()) return false;

    ensureValidationRules();

    if (form.dataset.sdsFormParsed !== "true") {
        $.validator.unobtrusive.parse(form);
        form.dataset.sdsFormParsed = "true";
    }

    configureValidator(form);

    return true;
}

export async function reinitFormValidation(form) {
    if (!(form instanceof HTMLFormElement)) return;
    if (!wantsValidation(form)) return;

    await validationStackReady;

    if (!hasValidationStack()) return;

    ensureValidationRules();

    const $form = $(form);
    const existingValidator = getValidator(form);

    if (existingValidator) {
        existingValidator.resetForm();
    }

    $form
        .removeData("validator")
        .removeData("unobtrusiveValidation");

    form.removeAttribute("data-sds-form-parsed");

    clearValidationState(form);

    $.validator.unobtrusive.parse(form);
    form.dataset.sdsFormParsed = "true";

    configureValidator(form);
}

export function destroyFormValidation(form) {
    if (!(form instanceof HTMLFormElement)) return;
    if (!$ || !$.validator) return;

    const $form = $(form);
    const existingValidator = getValidator(form);

    if (existingValidator) {
        existingValidator.resetForm();
    }

    $form
        .removeData("validator")
        .removeData("unobtrusiveValidation");

    form.removeAttribute("data-sds-form-parsed");
    clearValidationState(form);
}

function clearValidationState(form) {
    delete form.dataset.sdsSubmitting;
    delete form.dataset.sdsSubmitAllowed;

    form.classList.remove("is-valid", "is-invalid", "is-submitting");
    form.setAttribute("aria-busy", "false");

    form.querySelectorAll("[data-form-field]").forEach((field) => {
        field.classList.remove("is-valid", "is-invalid");
    });

    form.querySelectorAll(".input-validation-error").forEach((el) => {
        el.classList.remove("input-validation-error");
        el.removeAttribute("aria-invalid");
    });

    form.querySelectorAll("[data-valmsg-for]").forEach((el) => {
        el.textContent = "";
        el.classList.remove("field-validation-error");
        el.classList.add("field-validation-valid");
        delete el.dataset.formFieldMessageSource;
    });
}

function validateUploadFields(form) {
    let valid = true;
    let firstInvalidInput = null;

    form.querySelectorAll("[data-sds-upload='true']").forEach((upload) => {
        const event = new CustomEvent("sds-upload:validate", {
            bubbles: false,
            cancelable: true,
            detail: {
                focus: false,
            },
        });

        upload.dispatchEvent(event);

        if (event.defaultPrevented) {
            valid = false;

            if (!firstInvalidInput) {
                firstInvalidInput = upload.querySelector("[data-upload-input]");
            }
        }
    });

    return {
        valid,
        firstInvalidInput,
    };
}

function fire(form, name, detail) {
    form.dispatchEvent(new CustomEvent(name, { bubbles: true, detail }));
}

function fireValidated(form, valid) {
    form.dispatchEvent(
        new CustomEvent("sds:form:validated", {
            bubbles: true,
            detail: { valid },
        })
    );
}

function setSubmitting(form, submitting) {
    form.classList.toggle("is-submitting", submitting);
    form.setAttribute("aria-busy", submitting ? "true" : "false");
}

async function validateForm(form) {
    const uploadResult = validateUploadFields(form);
    const parsed = await ensureParsed(form);

    if (!parsed) {
        fireValidated(form, uploadResult.valid);
        return uploadResult;
    }

    const validator = getValidator(form);

    if (!validator) {
        fireValidated(form, uploadResult.valid);
        return uploadResult;
    }

    configureValidator(form);

    try {
        const jqueryValid = $(form).valid();
        const valid = uploadResult.valid && jqueryValid;

        fireValidated(form, valid);

        return {
            valid,
            firstInvalidInput: uploadResult.firstInvalidInput,
        };
    } catch (error) {
        console.error("Form validation failed", error);
        fireValidated(form, false);

        return {
            valid: false,
            firstInvalidInput: uploadResult.firstInvalidInput,
        };
    }
}

export function initForm(form) {
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.sdsFormBound === "true") return;

    ensureParsed(form);

    if (form.dataset.sdsFormAnalytics === "true") {
        fire(form, "sds:form:ready", {
            name: form.dataset.sdsFormName || null,
            context: form.dataset.sdsFormContext || null,
        });
    }

    form.addEventListener("submit", handleSubmit, true);
    form.addEventListener("reset", handleReset);

    form.dataset.sdsFormBound = "true";
}

function submitForm(form, submitter) {
    const temporarySubmitterInput = createTemporarySubmitterInput(submitter);
    if (temporarySubmitterInput) {
        form.appendChild(temporarySubmitterInput);
    }
        
    HTMLFormElement.prototype.submit.call(form);
}
function createTemporarySubmitterInput(submitter) {
    if (!(submitter instanceof HTMLElement)) return null;
    if (!submitter.matches("button, input")) return null;
    const name = submitter.getAttribute("name");
    const value = submitter.getAttribute("value");
    if (!name) return null;
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value || "";
    return input;
}

async function handleSubmit(event) {
    const form = event.currentTarget;

    if (!(form instanceof HTMLFormElement)) return;

    if (form.dataset.sdsSubmitAllowed === "true") {
        delete form.dataset.sdsSubmitAllowed;
        return;
    }

    event.preventDefault();

    const preventDouble = form.dataset.sdsFormPreventDoubleSubmit === "true";

    if (preventDouble && form.dataset.sdsSubmitting === "true") {
        return;
    }

    const result = await validateForm(form);

    if (!result.valid) {
        form.classList.remove("is-valid");
        form.classList.add("is-invalid");

        result.firstInvalidInput?.focus?.();

        if (form.dataset.sdsFormAnalytics === "true") {
            fire(form, "sds:form:invalid", {
                name: form.dataset.sdsFormName || null,
                context: form.dataset.sdsFormContext || null,
            });
        }

        return;
    }

    form.classList.remove("is-invalid");
    form.classList.add("is-valid");

    if (preventDouble) {
        form.dataset.sdsSubmitting = "true";
        setSubmitting(form, true);
    }

    const submitEvent = new CustomEvent("sds:form:beforeSubmit", {
        bubbles: true,
        cancelable: true,
        detail: { submitter: event.submitter },
    });

    form.dispatchEvent(submitEvent);

    if (submitEvent.defaultPrevented) {
        setSubmitting(form, false);
        delete form.dataset.sdsSubmitting;
        return;
    }

    if (form.dataset.sdsFormAnalytics === "true") {
        fire(form, "sds:form:submit", {
            name: form.dataset.sdsFormName || null,
            context: form.dataset.sdsFormContext || null,
        });
    }

    submitForm(form, event.submitter);
}

function handleReset(event) {
    const form = event.currentTarget;

    if (!(form instanceof HTMLFormElement)) return;

    delete form.dataset.sdsSubmitting;
    delete form.dataset.sdsSubmitAllowed;

    setSubmitting(form, false);

    form.classList.remove("is-valid", "is-invalid");

    window.requestAnimationFrame(() => {
        form.querySelectorAll("[data-form-field]").forEach((field) => {
            field.classList.remove("is-valid", "is-invalid");
        });
    });
}

document.addEventListener("dialog:contentready", async (event) => {
    const dialog = event.target;

    if (!(dialog instanceof HTMLElement)) return;

    const forms = dialog.querySelectorAll("form[data-sds-form-validate='true']");

    for (const form of forms) {
        initForm(form);
        await reinitFormValidation(form);
    }
});

document.addEventListener("dialog:beforeempty", (event) => {
    const dialog = event.target;

    if (!(dialog instanceof HTMLElement)) return;

    dialog
        .querySelectorAll("form[data-sds-form-validate='true']")
        .forEach(destroyFormValidation);
});

document.addEventListener("dialog:afterclose", (event) => {
    const dialog = event.target;

    if (!(dialog instanceof HTMLElement)) return;

    dialog
        .querySelectorAll("form[data-sds-form-validate='true']")
        .forEach(destroyFormValidation);
});